import React, { useEffect, useMemo, useState, useRef, KeyboardEvent, ChangeEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { showSuccess, showError } from "toast/toast";
import { Row } from "@tanstack/react-table";
import TablePaginationControls from "components/TableandmobilePaginationcontrol"
import { DesktopTableParts, MobileCardsOnly } from "components/TableandMobilecomponent";
import {
  DesktopTableLoader,
  MobileCardsLoader,
  DataTableStatus,
} from "components/DataTableLoader";
import { ViewIconButton, EditIconButton, DeleteIconButton } from "components/DatatablesActionIcons";
import DeleteConfirmModal from "components/ToolTips/DeleteConfirmModal";

import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  createColumnHelper,
} from "@tanstack/react-table";

import {
  ExportAsExcel,
} from "@siamf/react-export";

import { Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';


import { getAuthCookie } from "../../utils/authCookies";
import { useModuleActionPermissions } from "hooks/useModuleActionPermissions";
import { MODULE_PERMISSION_NAMES } from "utils/moduleActionPermissions";

interface SpecialFee {
  id: string;
  name: string;
  amount: number;
  status: boolean;
  required: boolean;
}

interface ExportRow {
  "S.No.": number;
  "Name": string;
  "Amount": string;
  "Required": string;
  "Status": string;
}

function fuzzyTextFilter<TData>(
  row: Row<TData>,
  columnId: string,
  filterValue: string
) {
  const raw = row.getValue(columnId);
  const value = (typeof raw === "boolean"
    ? raw
      ? "Active"
      : "Inactive"
    : String(raw)
  ).toLowerCase();

  return value.includes(filterValue.toLowerCase());
}

const columnHelper = createColumnHelper<SpecialFee>();

const SpecialFeeDatatable = () => {
  const [specialFees, setSpecialFees] = useState<SpecialFee[]>([]);
  const [filtered, setFiltered] = useState<SpecialFee[]>([]);
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState<string | null>(null);
  const token = getAuthCookie("sessionToken");
  const userId = getAuthCookie("userId");
  const navigate = useNavigate();
  const location = useLocation();

  // Determine the base route based on current location
  const isSignageTeammember = location.pathname.includes('/signageteammember');
  const baseRoute = isSignageTeammember ? '/signageteammember' : '/signage';

  const { canCreate, canView, canEdit, canDelete } = useModuleActionPermissions(
    MODULE_PERMISSION_NAMES.special_fees
  );

  const [toastShown, setToastShown] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const itemsPerPage = 10;
  const [rowCount, setRowCount] = useState(0);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [globalFilter, setGlobalFilter] = useState("");
  const [filteredSpecialFees, setFilteredSpecialFees] = useState<SpecialFee[]>([]);
  const totalPages = Math.ceil(filteredSpecialFees?.length / itemsPerPage);
  const [exportRows, setExportRows] = useState<ExportRow[]>([]);
  const [searchInput, setSearchInput] = useState<string>(globalFilter ?? "");
  const topRef = useRef<HTMLDivElement>(null);
  const hasShownToast = useRef(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);

  useEffect(() => {
    const fetchSpecialFees = async () => {
      setLoading(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/special-fees/datatable`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              page: pagination.pageIndex + 1,
              limit: pagination.pageSize,
              search: globalFilter,
              filters: {
                status: statusFilter || undefined,
              },
              sort: {
                field: "created_at",
                order: "desc",
              },
              fields: ["_id", "name", "amount", "status", "required"],
            }),
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch special fees");
        }

        const result = await response.json();
        setRowCount(result.total);

        const specialFeeData = result?.data ?? [];
        if (Array.isArray(specialFeeData)) {
          // Transform the data to match our interface
          const transformedData = specialFeeData.map((fee: any) => ({
            id: fee._id || fee.id,
            name: fee.name,
            amount: fee.amount,
            status: fee.status,
            required: fee.required,
          }));
          setSpecialFees(transformedData);
          setFilteredSpecialFees(transformedData);
        } else {
          throw new Error("Unexpected response format for special fees");
        }

      } catch (err) {
        setError(
          err instanceof Error ? err.message : "An unknown error occurred"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchSpecialFees();
  }, [token, globalFilter, pagination, deleting, statusFilter]);

  const handleDeleteClick = (fee: SpecialFee) => {
    setDeleteTarget({ id: fee.id, name: fee.name || "Special Fee" });
  };

  const handleDeleteCancel = () => {
    setDeleteTarget(null);
    setDeleting(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    await handleDelete(deleteTarget.id);
    setDeleteTarget(null);
  };

  const handleDelete = async (feeId: string) => {
    setDeleting(feeId);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/special-fees/${feeId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const result: any = await response.json();
      if (!response.ok || result?.error) {
        const message = result?.error || result?.message || "Failed to delete special fee";
        console.error("API Error:", message);
        showError(message);
        throw new Error(message);
      }
      showSuccess("Special fee deleted successfully!");
      setSpecialFees((prev) =>
        prev?.filter((fee) => fee.id !== feeId)
      );
      setDeleting(null);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "An unknown error occurred while deleting."
      );
      setDeleting(null);
    }
  };

  const handleToggleStatus = async (feeId: string, currentStatus: boolean) => {
    setDeleting(feeId);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/special-fees/${feeId}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            status: !currentStatus,
          }),
        }
      );

      const result: any = await response.json();
      if (!response.ok || result?.error) {
        const message = result?.error || result?.message || "Failed to update status";
        console.error("API Error:", message);
        showError(message);
        throw new Error(message);
      }

      showSuccess(`Special fee ${!currentStatus ? 'activated' : 'deactivated'} successfully!`);
      
      // Update fee status locally
      setSpecialFees(prev =>
        prev.map(fee =>
          fee.id === feeId
            ? { ...fee, status: !currentStatus }
            : fee
        )
      );
      setFilteredSpecialFees(prev =>
        prev.map(fee =>
          fee.id === feeId
            ? { ...fee, status: !currentStatus }
            : fee
        )
      );
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "An unknown error occurred while updating status."
      );
    } finally {
      setDeleting(null);
    }
  };

  const columns = useMemo(() => {
    return [
      columnHelper.display({
        id: "serial",
        header: () => <div className="th-text S-No">S.No.</div>,
        cell: (info) => (
          <div className="text-sm S-No">
            {pagination.pageIndex * pagination.pageSize + info.row.index + 1}
          </div>
        ),
      }),

      columnHelper.accessor("name", {
        id: "name",
        header: () => <div className="th-text S-No-2">Name</div>,
        cell: (info) => (
          <div className="text-sm S-No-2 fw-bold" style={{ color: '#155dfc'}}>
            {info.getValue()}
          </div>
        ),
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
      }),

      columnHelper.accessor("amount", {
        id: "amount",
        header: () => <div className="th-text">Amount</div>,
        cell: (info) => (
          <div className="text-sm fw-medium">${info.getValue().toFixed(2)}</div>
        ),
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
      }),

      columnHelper.accessor("required", {
        id: "required",
        header: () => <div className="th-text">Required</div>,
        cell: (info) => {
          const isRequired = info.getValue();
          return (
            <div className="text-sm">
              <span className={`badge rounded-pill fs-13 ${isRequired ? 'required-yes' : 'required-no'}`}>
                {isRequired ? "Yes" : "No"}
              </span>
            </div>
          );
        },
        enableGlobalFilter: false,
      }),

      columnHelper.accessor("status", {
        id: "status",
        header: () => <div className="th-text">Status</div>,
        cell: (info) => {
          const isActive = info.getValue();
          return (
            <div className="text-sm">
              <span className={`badge rounded-pill ${isActive ? 'status-badge badge-active' : 'status-badge badge-inactive'}`}>
                {isActive ? "Active" : "Inactive"}
              </span>
            </div>
          );
        },
        enableGlobalFilter: false,
      }),

      columnHelper.display({
        id: "actions",
        header: () => <div className="th-text">Actions</div>,
        cell: (info) => {
          const fee = info.row.original;
          return (
            <div className="d-flex align-items-center gap-2">
              {canView && (
              <ViewIconButton
                to={`${baseRoute}/view-special-fee/${fee.id}`} 
                className="btn action-btn" 
                title="View" 
              />
              )}

              {canEdit && (
              <EditIconButton
                to={`${baseRoute}/edit-special-fee/${fee.id}`} 
                state={{ type: "edit" }} 
                className="icon-square" 
                title="Edit" 
              />
              )}

              {canDelete && (
              <DeleteIconButton
                title="Delete"
                className="icon-square danger"
                onClick={() => handleDeleteClick(fee)}
                disabled={deleting === fee.id}
              />
              )}
            </div>
          );
        },
      }),
    ];
  }, [deleting, pagination, baseRoute, canView, canEdit, canDelete]);

  useEffect(() => {
    const rows: ExportRow[] = filteredSpecialFees.map((fee, index) => ({
      "S.No.": pagination.pageIndex * pagination.pageSize + index + 1,
      "Name": fee.name || '',
      "Amount": `$${fee.amount.toFixed(2)}`,
      "Required": fee.required ? 'Yes' : 'No',
      "Status": fee.status ? 'Active' : 'Inactive',
    }));

    setExportRows(rows);
  }, [filteredSpecialFees, pagination.pageIndex, pagination.pageSize]);

  const table = useReactTable({
    data: specialFees,
    columns,
    pageCount: Math.ceil(rowCount / pagination.pageSize),
    state: {
      pagination,
      globalFilter,
    },
    manualPagination: true,
    manualFiltering: true,
    onPaginationChange: setPagination,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
  });

  const pageCount = table.getPageCount();
  const currentPage = table.getState().pagination.pageIndex;

  useEffect(() => {
    if (topRef.current) {
      topRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [pagination.pageIndex]);

  return (
    <div className="content" ref={topRef}>
        <style>
            {
                `
                .required-yes {
                    background-color: #dbeafe;
                    color: #193cb8;
                }
                .required-no {
                    background-color: #f6f3f4;
                    color: #1e2939;
                }
                `
            }
        </style>
      <div className="d-flex justify-content-between align-items-center pb-3 mb-2 py-2">
        <h4 className="mb-0"></h4>
        <div className="d-flex gap-3 align-items-center">
          {canCreate && (
            <Link to={`${baseRoute}/add-special-fee`} state={{ type: "add" }} className="btn btn-primary table-addbutton">
            + Add Special Fee
          </Link>
          )}
        </div>
      </div>
      
      <div className="po-header mb-4 shadow-border-datatables">
        {/* TOP ROW */}
        <div className="po-top d-flex justify-content-between align-items-start align-items-md-center flex-wrap gap-2">
          <div className="po-title-wrap me-2">
            <h4 className="mb-1 po-title">Special Fees</h4>
          </div>

          {/* tools block (search + buttons) */}
          <div className="po-tools d-flex align-items-center flex-wrap gap-2 ms-auto">
            {/* Search */}
            <div className="input-group po-search">
              <span className="input-group-text">
                <i className="ri-search-line" />
              </span>
              <input
                type="text"
                id="searchInput"
                name="searchInput"
                className="form-control"
                placeholder="Search by Name, Amount..."
                value={searchInput}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchInput(e.target.value)}
                onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                  if (e.key === "Enter") {
                    setGlobalFilter(searchInput);
                  }
                }}
                autoComplete="off"
              />
            </div>

            {/* Filters toggle
            <button
              type="button"
              className={`po-filter-btn d-inline-flex align-items-center ${filtersOpen ? "active" : ""}`}
              onClick={() => setFiltersOpen(v => !v)}
              aria-expanded={filtersOpen}
              aria-controls="special-fees-filters"
            >
              <i className="ri-filter-3-line me-2" />
              <span>Filters</span>
              <i className={`ri-arrow-down-s-line ms-1 ${filtersOpen ? "rotate-180" : ""}`} />
            </button>
            
            <ExportAsExcel
              data={exportRows}
              headers={[
                "S.No.",
                "Name",
                "Amount",
                "Required",
                "Status",
              ]}
              fileName="SpecialFees"
            >
              {(props) => (
                <button {...props} className="export-button">
                  <i className="ri-file-excel-2-line me-2" />
                  Export to Excel
                </button>
              )}
            </ExportAsExcel> */}
          </div>
        </div>

        <div id="special-fees-filters" className={`po-filters ${filtersOpen ? "show" : ""}`}>
          <div className="po-filters-bar d-flex">
            <div className="row g-2 g-md-3 align-items-end justify-content-md-end ms-md-auto w-100 w-md-auto">
              <div className="col-md-4">
                <div className="po-title-wrap me-2 d-flex align-items-center">
                  <select 
                    className="form-select" 
                    value={statusFilter} 
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="">All Status</option>
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card overflow-hidden production-card box-shadow-datatables shadow-border-datatables">
        {/* Header like screenshot (left title, right count pill) */}
        <div className="align-items-center bg-white border-bottom d-flex justify-content-between px-4 py-4">
          <h5 className="mb-0">Special Fees</h5>
          <span className="badge rounded-pill bg-body-tertiary text-secondary-emphasis px-3 py-2 fs-13">
            {rowCount} fees
          </span>
        </div>
        <hr className="mb-2" style={{ borderTop: "none" }} />
        
        {/* DESKTOP (≥ 1022px) */}
        <div className="rdt-desktop shadow-sm">
          <div
            className="production-scroll"
            style={{ "--rdt-min": "1200px" } as React.CSSProperties}
          >
            <table className="table production-table align-middle mb-0 table-1">
              {loading ? (
                <DesktopTableLoader table={table} message="Loading special fees…" />
              ) : (
                <DesktopTableParts table={table} />
              )}
            </table>
          </div>
        </div>

        {/* MOBILE (≤ 1021px) */}
        <div className="rdt-mobile">
          {loading ? (
            <MobileCardsLoader message="Loading special fees…" />
          ) : (
            <MobileCardsOnly
            table={table}
            idColId="name"
            titleColId="name"
            actionColId="actions"
          />
          )}
        </div>
      </div>
      
      <DataTableStatus
        loading={loading}
        isEmpty={table.getRowModel().rows.length === 0}
      >
        <div className="mt-4 mb-4">
          <TablePaginationControls table={table} />
        </div>
      </DataTableStatus>
      <DeleteConfirmModal
        open={!!deleteTarget}
        dynamicName={deleteTarget?.name || "Special Fee"}
        onCancel={handleDeleteCancel}
        onDelete={handleDeleteConfirm}
      />
    </div>
  );
};

export default SpecialFeeDatatable;
